import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, RotateCcw, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const FIELD_ORDER = [
  { key: "title", label: "Title", input: "text" },
  { key: "eyebrow", label: "Eyebrow / kicker", input: "text" },
  { key: "tagline", label: "Tagline (one-liner shown under hero)", input: "textarea" },
  { key: "hero_image", label: "Hero image URL", input: "text" },
  { key: "intro", label: "Intro paragraph (Overview section)", input: "textarea" },
  { key: "seo_title", label: "SEO title (browser tab & Google)", input: "text" },
  { key: "seo_description", label: "SEO description (Google snippet)", input: "textarea" },
];

export default function AdminThemesEditor() {
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // slug being saved
  const [drafts, setDrafts] = useState({}); // { slug: { field: value } }

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/themes");
      setThemes(res.data);
      // Seed drafts with current merged values so admins can see & edit
      const seed = {};
      res.data.forEach((t) => {
        seed[t.slug] = { ...t.merged };
      });
      setDrafts(seed);
    } catch (e) {
      toast.error("Failed to load themes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setField = (slug, key, value) => {
    setDrafts((d) => ({ ...d, [slug]: { ...d[slug], [key]: value } }));
  };

  const save = async (slug) => {
    setSaving(slug);
    try {
      const t = themes.find((x) => x.slug === slug);
      const merged = drafts[slug] || {};
      // Only send fields that differ from default (so we don't create noise)
      const payload = {};
      FIELD_ORDER.forEach(({ key }) => {
        const val = merged[key] || "";
        const def = t.default[key] || "";
        if (val && val.trim() && val !== def) payload[key] = val;
        else payload[key] = null; // explicitly clear
      });
      await api.patch(`/admin/themes/${slug}`, payload);
      toast.success(`Saved override for /themes/${slug}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(null);
    }
  };

  const resetToDefault = async (slug) => {
    if (!window.confirm(`Reset /themes/${slug} back to the built-in default content?`)) return;
    setSaving(slug);
    try {
      await api.delete(`/admin/themes/${slug}/override`);
      toast.success(`Reset /themes/${slug} to defaults`);
      await load();
    } catch (e) {
      toast.error("Reset failed");
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="w-5 h-5 animate-spin inline" />
      </div>
    );
  }

  return (
    <div className="bg-white border border-cream-200 p-4 md:p-6" data-testid="admin-themes-editor">
      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-serif text-2xl text-deepred">Theme landing pages</h2>
          <p className="text-sm text-foreground/60 mt-1 max-w-2xl">
            Edit the copy, images and SEO of every <code className="text-xs bg-cream px-1.5 py-0.5 rounded">/themes/*</code> landing page.
            Leave a field empty to keep the built-in default.
          </p>
        </div>
      </div>

      <Accordion type="multiple" className="space-y-2">
        {themes.map((t) => {
          const draft = drafts[t.slug] || {};
          const hasOverride = t.override && Object.keys(t.override).length > 0;
          return (
            <AccordionItem
              key={t.slug}
              value={t.slug}
              data-testid={`theme-editor-${t.slug}`}
              className="border border-cream-200 rounded-sm"
            >
              <AccordionTrigger className="px-4 py-3 hover:no-underline">
                <div className="flex-1 flex items-center gap-3 text-left">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-saffron w-28 flex-shrink-0">
                    /themes/{t.slug}
                  </span>
                  <span className="text-sm font-medium">{t.merged.title}</span>
                  {hasOverride && (
                    <span className="ml-2 text-[9px] uppercase tracking-[0.2em] bg-marigold/30 text-foreground px-2 py-0.5 rounded-full">
                      Edited
                    </span>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-5">
                <div className="space-y-4">
                  {FIELD_ORDER.map(({ key, label, input }) => (
                    <div key={key}>
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-xs uppercase tracking-wider text-foreground/60">{label}</Label>
                        {draft[key] !== (t.default[key] || "") && (
                          <span className="text-[9px] uppercase tracking-[0.2em] text-marigold">Custom</span>
                        )}
                      </div>
                      {input === "textarea" ? (
                        <Textarea
                          rows={key === "intro" ? 6 : 3}
                          value={draft[key] || ""}
                          onChange={(e) => setField(t.slug, key, e.target.value)}
                          data-testid={`theme-${t.slug}-${key}`}
                          className="font-mono text-[13px]"
                        />
                      ) : (
                        <Input
                          value={draft[key] || ""}
                          onChange={(e) => setField(t.slug, key, e.target.value)}
                          data-testid={`theme-${t.slug}-${key}`}
                        />
                      )}
                      <div className="mt-1 text-[11px] text-foreground/40 truncate">
                        Default: {t.default[key] || "—"}
                      </div>
                    </div>
                  ))}
                  <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-cream-200">
                    <Button
                      onClick={() => save(t.slug)}
                      disabled={saving === t.slug}
                      data-testid={`save-theme-${t.slug}`}
                      className="bg-deepred hover:bg-saffron text-white"
                    >
                      {saving === t.slug ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
                      Save
                    </Button>
                    {hasOverride && (
                      <Button
                        variant="ghost"
                        onClick={() => resetToDefault(t.slug)}
                        disabled={saving === t.slug}
                        data-testid={`reset-theme-${t.slug}`}
                        className="text-foreground/60"
                      >
                        <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset to default
                      </Button>
                    )}
                    <a
                      href={`/themes/${t.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-auto text-xs uppercase tracking-[0.2em] text-deepred hover:text-saffron inline-flex items-center gap-1.5"
                      data-testid={`preview-theme-${t.slug}`}
                    >
                      Open live page <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}
